import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { finalize, Subject, switchMap, debounceTime, distinctUntilChanged, interval, firstValueFrom } from 'rxjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { BadgeComponent } from '../../../shared/components/badge/badge.component';
import { AlertType } from '../../../shared/models/alertType';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { VisitCycleService } from '../../../core/services/visit-cycle.service';
import {
  VisitCycleCard,
  VisitCycleCardsResult,
  VisitCycleCardFilters,
  VisitCycleFilterOptionsDto,
  VisitCycleStatus,
  ConsultantActivityTag,
  CycleCardBannerKind,
  getStatusLabel,
  getStatusVariant,
  getActivityTagLabel,
  getActivityTagVariant
} from '../ciclo/models/ciclo';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DateRangePickerComponent } from '../../../shared/components/date-range-picker/date-range-picker.component';
import { ListPreferencesService } from '../../../core/services/list-preferences.service';

interface FilterOption {
  value: string | number;
  label: string;
}

const PAGE_SIZE_OPTIONS: readonly number[] = [10, 15, 20];
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_PREFERENCE_LIST = 'ciclos-visita';

@Component({
  selector: 'app-ciclo-lista',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    MatMenuModule,
    FormsModule,
    BadgeComponent,
    NgSelectComponent,
    DateRangePickerComponent
  ],
  templateUrl: './ciclo-lista.component.html',
  styleUrl: './ciclo-lista.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CicloListaComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private listPreferences = inject(ListPreferencesService);
  private loadCardsSubject = new Subject<void>();
  private searchSubject = new Subject<string>();

  cards = signal<VisitCycleCard[]>([]);
  totalCount = signal(0);
  filteredCount = signal(0);
  page = signal(1);
  pageSize = signal(
    this.listPreferences.readPageSize(PAGE_SIZE_PREFERENCE_LIST, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE)
  );

  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  resultSummary = computed(() => {
    const shown = this.cards().length;
    const matching = this.filteredCount();

    if (matching === 0) {
      return 'Nenhum ciclo encontrado';
    }

    const noun = matching === 1 ? 'ciclo' : 'ciclos';

    return shown < matching
      ? `Exibindo os primeiros ${shown} de ${matching} ${noun}`
      : `Exibindo ${matching} ${noun}`;
  });

  isLoading = signal(false);
  isExportingPdf = signal(false);

  // Filter values
  searchTerm = signal('');
  selectedPeriodStart = signal<Date>(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  selectedPeriodEnd = signal<Date>(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
  selectedConsultant = signal<number | null>(null);
  selectedStatuses = signal<VisitCycleStatus[]>([]);
  selectedActivityTags = signal<ConsultantActivityTag[]>([]);

  // Filter options
  consultantOptions = signal<FilterOption[]>([]);
  
  readonly statusOptions: FilterOption[] = [
    { value: VisitCycleStatus.Draft, label: 'Rascunho' },
    { value: VisitCycleStatus.Active, label: 'Ativo' },
    { value: VisitCycleStatus.Paused, label: 'Pausado' },
    { value: VisitCycleStatus.Completed, label: 'Concluído' },
    { value: VisitCycleStatus.Cancelled, label: 'Cancelado' }
  ];

  readonly activityTagOptions: FilterOption[] = [
    { value: ConsultantActivityTag.OnRoute, label: 'Agora em Rota' },
    { value: ConsultantActivityTag.Paused, label: 'Pausado' },
    { value: ConsultantActivityTag.InternalActivity, label: 'Atividade Interna' },
    { value: ConsultantActivityTag.TimeBank, label: 'Banco de Horas' },
    { value: ConsultantActivityTag.Workshop, label: 'Workshop' },
    { value: ConsultantActivityTag.Visiting, label: 'Em Visita' },
    { value: ConsultantActivityTag.Offline, label: 'Offline' }
  ];

  readonly VisitCycleStatus = VisitCycleStatus;
  readonly ConsultantActivityTag = ConsultantActivityTag;

  constructor(
    private visitCycleService: VisitCycleService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializeFilters();

    this.loadCardsSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.isLoading.set(true);
          return this.visitCycleService.getCards(this.buildFilters()).pipe(
            finalize(() => this.isLoading.set(false))
          );
        })
      )
      .subscribe((result: VisitCycleCardsResult) => {
        this.cards.set(result.items);
        this.totalCount.set(result.totalCount);
        this.filteredCount.set(result.filteredCount);
      });

    this.searchSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe((term) => {
        this.searchTerm.set(term);
        this.page.set(1);
        this.loadCards();
      });

    this.loadCards();

    // Auto-refresh every 60 seconds for activity tag updates
    interval(60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadCards();
      });
  }

  private initializeFilters(): void {
    this.visitCycleService.getFilterOptions().subscribe({
      next: (options: VisitCycleFilterOptionsDto) => {
        this.consultantOptions.set(
          options.consultants.map(c => ({
            value: c.id,
            label: c.name
          }))
        );
      }
    });
  }

  private buildFilters(): VisitCycleCardFilters {
    const periodStart = this.formatDate(this.selectedPeriodStart());
    const periodEnd = this.formatDate(this.selectedPeriodEnd());

    const filters: VisitCycleCardFilters = {
      page: this.page(),
      pageSize: this.pageSize(),
      periodStart,
      periodEnd
    };

    const search = this.searchTerm();
    if (search) {
      filters.search = search;
    }

    const consultantId = this.selectedConsultant();
    if (consultantId) {
      filters.consultantId = consultantId;
    }

    const statuses = this.selectedStatuses();
    if (statuses.length === 1) {
      filters.status = statuses[0];
    }

    const tags = this.selectedActivityTags();
    if (tags.length === 1) {
      filters.activityTag = tags[0];
    }

    return filters;
  }

  loadCards(): void {
    this.loadCardsSubject.next();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onDateRangeChange(range: { startDate: Date; endDate: Date }): void {
    this.selectedPeriodStart.set(range.startDate);
    this.selectedPeriodEnd.set(range.endDate);
    this.page.set(1);
    this.loadCards();
  }

  onConsultantChange(event: FilterOption | null): void {
    this.selectedConsultant.set(event ? event.value as number : null);
    this.page.set(1);
    this.loadCards();
  }

  onStatusChange(events: FilterOption[] | null): void {
    this.selectedStatuses.set(events ? events.map(e => e.value as VisitCycleStatus) : []);
    this.page.set(1);
    this.loadCards();
  }

  onActivityTagChange(events: FilterOption[] | null): void {
    this.selectedActivityTags.set(events ? events.map(e => e.value as ConsultantActivityTag) : []);
    this.page.set(1);
    this.loadCards();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize.set(pageSize);
    this.listPreferences.writePageSize(PAGE_SIZE_PREFERENCE_LIST, pageSize);
    this.page.set(1);
    this.loadCards();
  }

  getStatusLabel(status: VisitCycleStatus): string {
    return getStatusLabel(status);
  }

  getStatusVariant(status: VisitCycleStatus): 'green' | 'yellow' | 'gray' | 'blue' | 'red' {
    return getStatusVariant(status);
  }

  getActivityTagLabel(tag: ConsultantActivityTag): string {
    return getActivityTagLabel(tag);
  }

  getActivityTagVariant(tag: ConsultantActivityTag): 'green' | 'yellow' | 'blue' | 'purple' | 'gray' | 'red' {
    return getActivityTagVariant(tag);
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getProgressBarWidth(progress: { percentComplete: number }): string {
    return `${Math.min(progress.percentComplete, 100)}%`;
  }

  readonly CycleCardBannerKind = CycleCardBannerKind;

  formatExecutedDate(date: string | null | undefined): string {
    if (!date) return '';
    // Backend envia 'YYYY-MM-DD'; banner usa formato curto 'dd/MM'.
    const parts = date.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}` : date;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  canEdit(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Draft;
  }

  // Edit de ciclo pós-ativação (consultor scoped + append de roteiros + edit de template).
  // Backend: UpdateVisitCycleCommandHandler aceita Draft (tudo), Active/Paused (restrito),
  // rejeita Completed/Cancelled.
  canEditCycle(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Draft
        || card.status === VisitCycleStatus.Active
        || card.status === VisitCycleStatus.Paused;
  }

  canDelete(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Draft;
  }

  canPause(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Active;
  }

  canResume(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Paused;
  }

  canCancel(card: VisitCycleCard): boolean {
    return card.status === VisitCycleStatus.Active || card.status === VisitCycleStatus.Paused;
  }

  navigateToNew(): void {
    this.router.navigate(['/ciclos-visita/novo']);
  }

  navigateToEdit(id: number): void {
    this.router.navigate(['/ciclos-visita/editar', id]);
  }

  navigateToCalendar(id: number): void {
    this.router.navigate(['/ciclos-visita/editar', id, 'ajustar-calendario']);
  }

  onCardClick(card: VisitCycleCard): void {
    if (this.canEdit(card)) {
      this.navigateToEdit(card.id);
    } else {
      this.navigateToCalendar(card.id);
    }
  }

  confirmDelete(card: VisitCycleCard, event: Event): void {
    event.stopPropagation();
    showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Excluir Ciclo',
      `Tem certeza que deseja excluir o ciclo "${card.name}"? Esta ação não pode ser desfeita.`,
      'Excluir',
      'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteCycle(card.id);
      }
    });
  }

  private deleteCycle(id: number): void {
    this.visitCycleService.deleteVisitCycle(id).subscribe({
      next: () => {
        this.loadCards();
      },
      error: (err) => {
        console.error('Erro ao excluir ciclo:', err);
      }
    });
  }

  confirmPause(card: VisitCycleCard, event: Event): void {
    event.stopPropagation();
    showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Pausar Ciclo',
      `Deseja pausar o ciclo "${card.name}"?`,
      'Pausar',
      'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.visitCycleService.pause(card.id).subscribe({
          next: () => this.loadCards(),
          error: (err) => {
            console.error('Erro ao pausar ciclo:', err);
          }
        });
      }
    });
  }

  confirmResume(card: VisitCycleCard, event: Event): void {
    event.stopPropagation();
    showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Retomar Ciclo',
      `Deseja retomar o ciclo "${card.name}"?`,
      'Retomar',
      'Cancelar'
    ).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.visitCycleService.resume(card.id).subscribe({
          next: () => this.loadCards(),
          error: (err) => {
            console.error('Erro ao retomar ciclo:', err);
          }
        });
      }
    });
  }

  confirmCancel(card: VisitCycleCard, event: Event): void {
    event.stopPropagation();
    showAlertDialog(
      this.dialog,
      AlertType.Warning,
      'Cancelar Ciclo',
      `Deseja cancelar o ciclo "${card.name}"? Esta ação encerra o ciclo definitivamente.`,
      'Cancelar ciclo',
      'Voltar'
    ).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.visitCycleService.cancel(card.id).subscribe({
          next: () => this.loadCards(),
          error: (err) => {
            console.error('Erro ao cancelar ciclo:', err);
          }
        });
      }
    });
  }

  async exportToPdf(): Promise<void> {
    if (this.isExportingPdf()) {
      return;
    }

    this.isExportingPdf.set(true);

    try {
      const cards = await this.loadAllCardsForExport();
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const margin = 36;
      let cursorY = 44;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(15, 23, 42);
      pdf.text('Ciclos de Visitação', margin, cursorY);
      cursorY += 22;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Período: ${this.formatDate(this.selectedPeriodStart())} até ${this.formatDate(this.selectedPeriodEnd())}`, margin, cursorY);
      cursorY += 14;
      pdf.text(`Consultor: ${this.getConsultantFilterLabel()}`, margin, cursorY);
      cursorY += 14;
      pdf.text(`Status: ${this.getStatusFilterLabel()}`, margin, cursorY);
      cursorY += 14;
      pdf.text(`Atividade: ${this.getActivityTagFilterLabel()}`, margin, cursorY);
      cursorY += 14;
      pdf.text(`Busca: ${this.searchTerm().trim() || 'Sem filtro'}`, margin, cursorY);
      cursorY += 14;
      pdf.text(`Gerado em: ${this.formatExportDate(new Date())}`, margin, cursorY);
      cursorY += 18;

      if (cards.length === 0) {
        autoTable(pdf, {
          startY: cursorY,
          margin: { left: margin, right: margin },
          theme: 'grid',
          head: [['Mensagem']],
          body: [['Nenhum ciclo encontrado para os filtros selecionados.']],
          styles: {
            font: 'helvetica',
            fontSize: 10,
            cellPadding: 8,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [241, 245, 249],
            textColor: [15, 23, 42],
            fontStyle: 'bold',
          },
        });
      } else {
        autoTable(pdf, {
          startY: cursorY,
          margin: { left: margin, right: margin },
          theme: 'grid',
          head: [[
            'Ciclo',
            'Consultor',
            'Status',
            'Atividade',
            'Período',
            'Rota Atual',
            'Dias',
            'Lojas',
            'Km',
            'Progresso',
          ]],
          body: cards.map(card => [
            card.name,
            card.consultant.name,
            card.statusLabel,
            card.activityTagLabel,
            card.periodLabel,
            this.getRouteBannerLabel(card),
            `${card.progress.completedDays}/${card.progress.totalDays}`,
            String(card.totalStores),
            this.formatKilometers(card.totalKm),
            `${card.progress.percentComplete}%`,
          ]),
          styles: {
            font: 'helvetica',
            fontSize: 9,
            cellPadding: 6,
            textColor: [51, 65, 85],
            valign: 'middle',
          },
          headStyles: {
            fillColor: [15, 23, 42],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 150 },
            1: { cellWidth: 110 },
            2: { cellWidth: 70 },
            3: { cellWidth: 85 },
            4: { cellWidth: 130 },
            5: { cellWidth: 130 },
            6: { cellWidth: 55, halign: 'center' },
            7: { cellWidth: 50, halign: 'center' },
            8: { cellWidth: 55, halign: 'right' },
            9: { cellWidth: 60, halign: 'center' },
          },
        });
      }

      pdf.save(`ciclos-visitacao-${this.formatDateForFileName(this.selectedPeriodStart())}-${this.formatDateForFileName(this.selectedPeriodEnd())}.pdf`);
    } catch (error) {
      console.error('Erro ao exportar ciclos em PDF:', error);
      showAlertDialog(this.dialog, AlertType.Error, 'Erro', 'Não foi possível exportar os ciclos em PDF.');
    } finally {
      this.isExportingPdf.set(false);
    }
  }

  private async loadAllCardsForExport(): Promise<VisitCycleCard[]> {
    const pageSize = 200;
    const baseFilters = this.buildFilters();
    const firstPage = await firstValueFrom(this.visitCycleService.getCards({
      ...baseFilters,
      page: 1,
      pageSize,
    }));

    const cards = [...firstPage.items];
    const totalFiltered = firstPage.filteredCount;

    for (let page = 2; cards.length < totalFiltered; page += 1) {
      const response = await firstValueFrom(this.visitCycleService.getCards({
        ...baseFilters,
        page,
        pageSize,
      }));

      if (response.items.length === 0) {
        break;
      }

      cards.push(...response.items);
    }

    return cards;
  }

  private getConsultantFilterLabel(): string {
    const consultantId = this.selectedConsultant();
    if (!consultantId) {
      return 'Todos';
    }

    return this.consultantOptions().find(option => option.value === consultantId)?.label ?? 'Consultor selecionado';
  }

  private getStatusFilterLabel(): string {
    const statuses = this.selectedStatuses();
    if (statuses.length === 0) {
      return 'Todos';
    }

    return this.statusOptions
      .filter(option => statuses.includes(option.value as VisitCycleStatus))
      .map(option => option.label)
      .join(', ');
  }

  private getActivityTagFilterLabel(): string {
    const tags = this.selectedActivityTags();
    if (tags.length === 0) {
      return 'Todos';
    }

    return this.activityTagOptions
      .filter(option => tags.includes(option.value as ConsultantActivityTag))
      .map(option => option.label)
      .join(', ');
  }

  private getRouteBannerLabel(card: VisitCycleCard): string {
    if (!card.banner) {
      return '-';
    }

    if (card.banner.kind === CycleCardBannerKind.CurrentRoute) {
      return `${card.banner.dayNumber} | ${card.banner.dayTitle}`;
    }

    const executedDate = this.formatExecutedDate(card.banner.executedDate);
    return `${executedDate} - ${card.banner.dayNumber} | ${card.banner.dayTitle}`;
  }

  private formatExportDate(date: Date): string {
    const datePart = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);

    const timePart = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);

    return `${datePart} às ${timePart}`;
  }

  private formatDateForFileName(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private formatKilometers(value: number): string {
    return `${value.toFixed(1)} km`;
  }
}
